import { Box, Typography } from "@mui/material";
import { statusVisual } from "@/constants/campaings";

interface StatusDotProps {
  /** Diameter in px. */
  size: number;
  /** CSS color for the dot. */
  color: string;
  /** Animate opacity 0.3 -> 1 -> 0.3 over 1.1s, for a state that is happening right now. */
  pulse?: boolean;
}

/**
 * The colored bullet that precedes a status label. Exported because the Send Queue's Scheduled
 * cell shows the same in-flight marker outside a chip, and both must read from one definition.
 */
export function StatusDot({ size, color, pulse = false }: StatusDotProps) {
  return (
    <Box
      component="span"
      aria-hidden="true"
      sx={{
        display: "inline-block",
        width: size,
        height: size,
        borderRadius: "50%",
        bgcolor: color,
        flexShrink: 0,
        ...(pulse && {
          animation: "status-dot-pulse 1.1s ease-in-out infinite",
          "@keyframes status-dot-pulse": {
            "0%": { opacity: 0.3 },
            "50%": { opacity: 1 },
            "100%": { opacity: 0.3 },
          },
        }),
      }}
    />
  );
}

interface CampaignStatusChipProps {
  /**
   * Any campaign status — the campaign's own (`draft`/`scheduled`/`active`/`closed`, plus the
   * legacy `archived`/`completed` spellings), a campaign-link status
   * (`Pending`/`Sent`/`Redeemed`/`Expired`/`Bounced`), or a send-queue status
   * (`Live`/`Sending`/`Paused`, `Scheduled` being shared). Matching is case-insensitive and an
   * unrecognised value is drawn neutrally rather than mislabelled — see `statusVisual`.
   */
  status: string;
  /** Show the colored bullet dot before the label. Default: true */
  showDot?: boolean;
  /**
   * sm — smaller padding, Title Case label (e.g. "Active")
   * md — standard padding, ALL CAPS label (e.g. "ACTIVE"), matches overline style
   * Default: "md"
   */
  size?: "sm" | "md";
}

/**
 * THE status chip. One component for every status a campaign can show, at any level: the
 * campaign's own state, the per-recipient link state and the per-send queue state all come from
 * the same palette (`STATUS_VISUALS` in `constants/campaings.ts`), so there is one place to add a
 * status and one place that decides how a status looks.
 */
export default function CampaignStatusChip({
  status,
  showDot = true,
  size = "md",
}: CampaignStatusChipProps) {
  const cfg = statusVisual(status);
  const isSm = size === "sm";

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: isSm ? 0.375 : 0.5,
        bgcolor: cfg.bgColor,
        borderRadius: "20px",
        px: isSm ? 1.2 : "10px",
        py: isSm ? 0.4 : "2px",
        flexShrink: 0,
      }}
    >
      {showDot && <StatusDot size={isSm ? 5 : 6} color={cfg.dotColor} pulse={cfg.pulse} />}
      <Typography
        sx={{
          color: cfg.textColor,
          lineHeight: 1,
          fontFamily: "var(--font-lato)",
          fontSize: isSm ? 13 : 11,
          fontWeight: isSm ? 600 : 700,
          letterSpacing: isSm ? 0 : "1px",
          textTransform: isSm ? "capitalize" : "uppercase",
          ...(cfg.strike && {
            textDecoration: "line-through",
            textDecorationColor: cfg.strikeColor ?? "currentColor",
          }),
        }}
      >
        {cfg.label}
      </Typography>
    </Box>
  );
}
